import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { Scale, Target, TrendingUp, TrendingDown, Dumbbell, Info, Heart, Ruler, ChevronRight } from "lucide-react";

export default function IdealWeight() {
  const [profile, setProfile] = useState(null);
  useEffect(() => { api.get("/profile").then((r) => setProfile(r.data)); }, []);

  if (!profile) return <AppShell title="Mi meta saludable"><div className="text-[var(--mep-text-muted)]">Cargando...</div></AppShell>;

  const rmin = profile.range_min_kg || 0;
  const rmax = profile.range_max_kg || 0;
  const direction = profile.initial_target_direction || "mantener";
  const current = profile.weight_kg;
  const msLow = profile.first_milestone_low_kg || 0;
  const msHigh = profile.first_milestone_high_kg || 0;
  const hasMilestone = msLow > 0 && msHigh > 0 && direction !== "mantener";
  const wantsMuscle = profile.goal === "ganar_masa";

  // Milestone band label: show "84–88 kg" or single value if collapsed
  const bandDisplay = hasMilestone
    ? (Math.abs(msHigh - msLow) < 0.15
        ? `${msLow.toFixed(1)} kg`
        : `${Math.min(msLow, msHigh).toFixed(1)} – ${Math.max(msLow, msHigh).toFixed(1)} kg`)
    : null;

  const isLosing = direction === "bajar";
  const MilestoneIcon = isLosing ? TrendingDown : TrendingUp;
  const milestoneLabel = hasMilestone
    ? (isLosing ? "Meta recomendada inicial" : "Meta recomendada inicial")
    : "Mantén tu progreso";

  return (
    <AppShell
      title="Mi meta saludable"
      subtitle="Vamos paso a paso. Una primera meta realista y sostenible, no un número extremo."
    >
      <div className="grid lg:grid-cols-3 gap-6 max-w-5xl" data-testid="ideal-weight-page">
        {/* PRIMARY CARD: Meta recomendada inicial (absolute band) */}
        <div className="mep-card p-8 col-span-2">
          <div className="flex items-start gap-5">
            <div className="w-14 h-14 rounded-full bg-[rgba(232,61,132,0.12)] border border-[var(--mep-pink)]/40 flex items-center justify-center text-[var(--mep-pink)]">
              <MilestoneIcon size={26} />
            </div>
            <div className="flex-1">
              <div className="text-eyebrow mb-1">{milestoneLabel}</div>
              {hasMilestone ? (
                <>
                  <div
                    className="font-display text-5xl md:text-6xl font-light tracking-tighter text-[var(--mep-pink)]"
                    data-testid="first-milestone-band"
                  >
                    {bandDisplay}
                  </div>
                  <p className="text-xs text-[var(--mep-text-muted)] mt-3 max-w-md leading-relaxed">
                    Reducción aproximada del 5–10% de tu peso actual. Una primera meta pequeña
                    es la más estudiada y sostenible. Cuando la alcances, redefiniremos juntos la siguiente etapa.
                  </p>
                </>
              ) : (
                <>
                  <div
                    className="font-display text-4xl md:text-5xl font-light tracking-tighter text-[var(--mep-gold)]"
                    data-testid="first-milestone-band"
                  >
                    Mantén tu progreso
                  </div>
                  <p className="text-xs text-[var(--mep-text-muted)] mt-3 max-w-md leading-relaxed">
                    Estás dentro de un rango saludable. Enfócate en constancia, entrenamiento y bienestar general.
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-8 pt-8 border-t border-[var(--mep-border)]">
            <Stat label="Peso actual" value={`${current} kg`} icon={Scale} testid="current-weight-stat" />
            <Stat
              label="Siguiente etapa"
              value={hasMilestone ? bandDisplay : "—"}
              icon={Target}
              accent={hasMilestone}
              testid="next-stage-stat"
            />
          </div>

          {/* Long-term reference (secondary) */}
          <div
            className="mt-8 p-5 rounded-lg bg-[var(--mep-elev)] border border-[var(--mep-border)]"
            data-testid="long-term-range-card"
          >
            <div className="flex items-center gap-2 text-[var(--mep-gold)] mb-2">
              <Info size={16} />
              <span className="text-xs uppercase tracking-[0.2em]">Rango de referencia a largo plazo</span>
            </div>
            <div className="font-display text-2xl md:text-3xl font-light tracking-tight">
              {rmin} <span className="text-lg text-[var(--mep-text-muted)] mx-1">–</span> {rmax}
              <span className="text-lg text-[var(--mep-text-muted)] ml-2">kg</span>
            </div>
            <p className="text-xs text-[var(--mep-text-muted)] mt-2 leading-relaxed">
              Solo como referencia teórica basada en IMC, estatura, sexo y edad. No es el número que debes alcanzar
              inmediatamente ni una meta para hoy.
            </p>
          </div>
        </div>

        {/* SIDE CARD: Tu objetivo */}
        <div className="mep-card p-8 flex flex-col gap-6">
          <div>
            <div className="text-eyebrow mb-3">Tu objetivo</div>
            <div className="font-display text-2xl md:text-3xl font-light capitalize" data-testid="user-goal">
              {profile.goal.replace("_", " ")}
            </div>
          </div>

          {wantsMuscle ? (
            <div className="p-4 rounded-lg bg-[rgba(212,175,55,0.08)] border border-[var(--mep-gold)]/30">
              <div className="flex items-center gap-2 text-[var(--mep-gold)] mb-2">
                <Dumbbell size={16} />
                <span className="text-xs uppercase tracking-[0.2em]">Composición corporal</span>
              </div>
              <p className="text-sm text-[var(--mep-text-muted)] leading-relaxed">
                Si entrenas con cargas, tu peso saludable puede estar por encima de este rango.
                El músculo pesa más que la grasa: prioriza medidas y espejo, no solo la báscula.
              </p>
            </div>
          ) : (
            <div className="p-4 rounded-lg bg-[var(--mep-elev)] border border-[var(--mep-border)]">
              <div className="flex items-center gap-2 text-[var(--mep-text-muted)] mb-2">
                <Info size={16} />
                <span className="text-xs uppercase tracking-[0.2em]">Importante</span>
              </div>
              <p className="text-sm text-[var(--mep-text-muted)] leading-relaxed">
                El número en la báscula es solo una parte. Combina alimentación, entrenamiento y
                descanso para resultados sostenibles. Tu cuerpo es único.
              </p>
            </div>
          )}
        </div>

        {/* MOTIVATIONAL BLOCK (full width) */}
        <div
          className="col-span-2 lg:col-span-3 mep-card p-8 border-l-4 border-[var(--mep-pink)]"
          data-testid="motivational-block"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[rgba(232,61,132,0.12)] border border-[var(--mep-pink)]/40 flex items-center justify-center text-[var(--mep-pink)] flex-shrink-0">
              <Heart size={22} />
            </div>
            <div className="text-sm md:text-base text-[var(--mep-text)] leading-relaxed space-y-3">
              <p>
                <strong className="text-[var(--mep-gold)]">Tu progreso no se mide por llegar de golpe a un número perfecto.</strong>
              </p>
              <p className="text-[var(--mep-text-muted)]">
                Si tienes mucho peso por perder, avanzar poco a poco puede ser más realista y sostenible.
                Tu primera meta es solo el comienzo. Cuando la alcances, volveremos a evaluar tu progreso
                y definiremos juntos el siguiente objetivo.
              </p>
              <p className="text-[var(--mep-text-muted)]">
                Cada kilo perdido, cada centímetro reducido y cada hábito saludable cuenta.
                No compares tu cuerpo con una tabla. Tu verdadera meta es sentirte más fuerte, saludable
                y capaz de mantener tus resultados a largo plazo.
              </p>
            </div>
          </div>
        </div>

        {/* CTA to full personalised plan */}
        <Link
          to="/mi-plan"
          data-testid="idealweight-go-to-plan"
          className="col-span-2 lg:col-span-3 mep-card p-6 flex items-center justify-between hover:border-[var(--mep-pink)]/40 transition group"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[rgba(232,61,132,0.12)] border border-[var(--mep-pink)]/40 flex items-center justify-center text-[var(--mep-pink)]">
              <Ruler size={22} />
            </div>
            <div>
              <div className="text-eyebrow text-[var(--mep-pink)] mb-1">Siguiente paso</div>
              <div className="font-display text-xl font-light">Mi plan personalizado por medidas</div>
              <p className="text-sm text-[var(--mep-text-muted)] mt-1">
                Rangos orientativos de cintura, cadera, glúteo, muslo + rutinas priorizadas para ti.
              </p>
            </div>
          </div>
          <ChevronRight className="text-[var(--mep-pink)] group-hover:translate-x-1 transition" />
        </Link>
      </div>
    </AppShell>
  );
}

const Stat = ({ label, value, icon: Icon, accent, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center gap-2 text-[var(--mep-text-muted)] text-xs uppercase tracking-[0.2em]">
      <Icon size={14} /> {label}
    </div>
    <div className={`font-display text-2xl md:text-3xl font-light mt-2 ${accent ? "text-[var(--mep-pink)]" : ""}`}>{value}</div>
  </div>
);
